import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Affix from "antd/es/affix";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import EditCardModal from "@app/scene/dashboard/components/addEditCard/EditCardModal";
import Grid from "@app/scene/dashboard/components/DashboardContent/components/Grid";
import useGridCards, {
  GridCardInfo,
} from "@app/scene/dashboard/components/DashboardContent/util/useGridCards";
import { addCard, removeCard, saveCard } from "@app/scene/dashboard/store/actions";
import { getBoards } from "@app/scene/dashboard/store/selectors";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import { getCurrentUser, isCumulEnabled } from "@app/store/iam";
import { isManager, isMember, isReadOnly } from "@app/store/iam/util";
import { RootState } from "@app/store/rootReducer";
import CardConfiguration, { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Dashboard from "@app/types/dashboard/Dashboard";
import DashboardCard from "@app/types/dashboard/DashboardCard";
import Scope from "@app/types/dashboard/Scope";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";

import AddCardWizard from "../addEditCard/AddCardWizard";

import EmptyGrid from "./components/Grid/EmptyGrid";
import TeamMembersAlert from "./components/TeamMembersAlert";
import styles from "./DashboardContent.module.scss";
import useAddCard from "./util/useAddCard";

interface Props {
  boards: Dashboard[];
  className?: string;
  currentUser: User;
  isCumulEnabled?: boolean;
  onAddCard?: (payload: { card: DashboardCard; scope: Scope }) => void;
  onRemoveCard?: (payload: { cardId: DashboardCard["id"]; scope: Scope }) => void;
  onSaveCard?: (payload: {
    cardId: DashboardCard["id"];
    configuration: CardConfiguration<any, any, any>;
    scope: Scope;
    teamGoalConfiguration?: GoalConfiguration;
    updateGoalOverrides: boolean;
    updateMetaData?: Pick<DashboardCard, "updatedAt" | "updatedBy">;
    updatePreferencesOverrides: boolean;
  }) => void;
  scope: Scope;
}

const DashboardContent: React.FC<Props> = ({
  boards,
  className,
  currentUser,
  isCumulEnabled,
  onAddCard,
  onRemoveCard,
  onSaveCard,
  scope,
}) => {
  const analytics = useExtendedAnalytics("Dashboard", useAnalytics());
  const intl = useIntl();

  const [addCardWizardVisible, showAddCardWizard, hideAddCardWizard] = useBoolean();

  const cards = useGridCards(boards, scope, currentUser);

  const handleAddCard = useAddCard(boards, currentUser, scope, onAddCard);

  const [cardBeingEdited, setCardToEdit] = useState<GridCardInfo | undefined>();
  const handleEdit = useCallback(
    (cardId: DashboardCard["id"]) => setCardToEdit(cards.find((card) => card.id === cardId)),
    [cards]
  );

  const handleCreateCopy = useCallback(
    (cardId: DashboardCard["id"]) => {
      const cardToCopy = cards.find((card) => card.id === cardId);
      if (cardToCopy) {
        const { x, y } = cardToCopy.layout;
        // Dashboard auto arranges the cards the puts the new card exactly below the previous card
        handleAddCard(
          cardToCopy.type,
          cardToCopy.configuration,
          { x, y },
          cardToCopy.viewState.fullWidth
        );
      }
    },
    [cards, handleAddCard]
  );

  const handleHideEditModal = useCallback(() => setCardToEdit(undefined), []);
  const handleSaveCard = useCallback(
    (configuration: CardConfiguration<any, any, any>, updatedTeamGoal?: GoalConfiguration) => {
      if (!cardBeingEdited) {
        return;
      }
      const editingOwnBoard = scope.userId === currentUser.id && isMember(currentUser);
      const isTeamUserScope = !!scope.teamId && !!scope.userId;
      const shouldUpdateMetaData =
        (!isMember(currentUser) && !isReadOnly(currentUser)) || !isTeamUserScope;

      onSaveCard?.({
        cardId: cardBeingEdited.id,
        configuration,
        scope,
        teamGoalConfiguration: updatedTeamGoal,
        updateGoalOverrides: editingOwnBoard || (isTeamUserScope && !updatedTeamGoal?.enabled),
        updateMetaData: shouldUpdateMetaData
          ? {
              updatedAt: new Date().toISOString(),
              updatedBy: { id: currentUser.id },
            }
          : undefined,
        updatePreferencesOverrides: editingOwnBoard,
      });
    },
    [cardBeingEdited, onSaveCard, scope, currentUser]
  );

  const handleRemoveCard = useCallback(
    (cardId: DashboardCard["id"]) => onRemoveCard?.({ cardId, scope }),
    [onRemoveCard, scope]
  );

  const canAddCharts = canEditBoard(currentUser, scope, "add");
  const canEditLayout = canEditBoard(currentUser, scope, "editLayout");

  const teamGoal = useMemo(() => {
    if (
      !cardBeingEdited ||
      !scope.teamId ||
      !scope.userId ||
      boards.length < 2 ||
      // we don't need teamGoal for manager's own board
      (isManager(currentUser) && currentUser.id === scope.userId)
    ) {
      return undefined;
    }
    const teamBoard = boards.find((board) => board.teamId === scope.teamId && !board.userId);
    const parentCard = teamBoard?.settings.cards.find((card) => card.id === cardBeingEdited.id);
    return parentCard?.configuration.goal;
  }, [boards, scope, cardBeingEdited, currentUser]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <div className={className}>
        <TeamMembersAlert currentUser={currentUser} scope={scope} />
        {cards.length > 0 ? (
          <Grid
            cards={cards}
            draggable={canEditLayout}
            onCopy={handleCreateCopy}
            onEdit={handleEdit}
            onRemove={handleRemoveCard}
            scope={scope}
          />
        ) : (
          <EmptyGrid
            canAddCharts={canAddCharts}
            onAddChart={canAddCharts ? showAddCardWizard : undefined}
          />
        )}
        {isCumulEnabled && (
          <div className={styles.footerButtons}>
            <span>
              {intl.formatMessage({
                id: "dashboard.grid.accessReports",
                defaultMessage: "Access Advanced Reports",
                description: "Access luzmo reports text",
              })}
            </span>
            <Button href={`${Path.REPORTS}/all-dashboards`} type="primary">
              {intl.formatMessage({
                id: "dashboard.grid.viewReports",
                defaultMessage: "View Reports",
                description: "Access luzmo reports button",
              })}
            </Button>
          </div>
        )}
        {canAddCharts && (
          <Affix className={styles.addButtonContainer} offsetBottom={13 + 48 + 12}>
            <Tooltip
              placement="left"
              title={intl.formatMessage({
                id: "dashboard.grid.add.tooltip",
                defaultMessage: "Add Chart",
                description: "Tooltip for the floating Add Chart button on the Dashboard Grid view",
              })}
            >
              <Button
                className={styles.addButton}
                icon={<FontAwesomeIcon icon={faPlus} />}
                iconOnly
                onClick={showAddCardWizard}
                size="large"
                type="primary"
              />
            </Tooltip>
          </Affix>
        )}
      </div>
      {addCardWizardVisible && (
        <AddCardWizard onAddCard={handleAddCard} onHide={hideAddCardWizard} scope={scope} />
      )}
      {cardBeingEdited ? (
        <EditCardModal
          configuration={cardBeingEdited.configuration}
          details={cardBeingEdited.metaData}
          onChange={handleSaveCard}
          onHide={handleHideEditModal}
          scope={scope}
          teamGoal={teamGoal}
          type={cardBeingEdited.type}
        />
      ) : null}
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  boards: getBoards(state),
  currentUser: getCurrentUser(state)!,
  isCumulEnabled: isCumulEnabled(state),
});

const mapDispatchToProps = {
  onAddCard: addCard.request,
  onRemoveCard: removeCard.request,
  onSaveCard: saveCard.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardContent);
